import { css, unsafeCSS } from 'lit';

/** The notices' fade-out (legacy `.hidemsg`): drives both the `.hiding`
 *  animation below and the consumers' second timer, which clears the state as
 *  the fade ends — one number, so the two can't drift apart. */
export const NOTICE_FADE_MS = 1500;

/** Widest viewport on the notices' compact sizes (the tablet and phone
 *  breakpoints below). Consumers cap long names at or under it — the chip has
 *  no room for a 20-character name there, and plenty above it. */
export const NOTICE_COMPACT_MAX_WIDTH = 1024;

/** The compact viewport: at or under this width or height (a phone, a short
 *  window) the transient notices stack in airmash-game's column
 *  (`.notice-lanes`) rather than each sitting in its own band. The notices'
 *  `(max-width), (max-height)` rules are this number, and layout.ts's
 *  noticesStack is its TS side. */
export const NOTICE_COLUMN_MAX_PX = 599;

/**
 * Shared shell for the kill and death notices ("You have destroyed…" /
 * "Destroyed by…"). Both are full-viewport, click-through overlays that pop a
 * centred `.notice` in, fade it out via `.hiding`, and draw player chips (a
 * flag and a level or bot badge) over score and upgrade lines. This holds
 * everything the two render identically, the chip included; each keeps its
 * own `.notice` placement and size.
 *
 * Usage: `static override styles = [tokens, noticeStyles, css\`...\`];`
 */
export const noticeStyles = css`
  :host {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 50;
    /* Declared here rather than inherited from the body — the component owns
       its own typography. Click-through, so no selection or tap rules needed. */
    font-family: var(--font-family);
  }

  .notice {
    position: absolute;
    text-align: center;
    font-weight: 800;
    color: #f0f0f0;
    text-shadow: var(--shadow-notice);
    transform: translateY(-50%);
    animation: msgshow 0.4s ease-in-out 1;
    animation-fill-mode: forwards;
  }

  .notice.hiding {
    animation: msghide ${unsafeCSS(NOTICE_FADE_MS)}ms linear 1;
    animation-fill-mode: forwards;
  }

  @keyframes msgshow {
    0% {
      opacity: 1;
    }
    1% {
      transform: translateY(-50%) scale(1);
    }
    30% {
      transform: translateY(-50%) scale(1.25);
    }
    100% {
      transform: translateY(-50%) scale(1);
    }
  }

  @keyframes itempop {
    0% {
      transform: scale(0.5);
      opacity: 0;
    }
    50% {
      transform: scale(1.3);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes msghide {
    0% {
      opacity: 1;
      transform: translateY(-50%) scale(1);
    }
    100% {
      opacity: 0;
      transform: translateY(-50%) scale(1);
    }
  }

  /* The player chip: legacy's .player (the kill notice) and .playerbig (the
     death notice's, with a little more room against its bigger heading). */
  .player,
  .playerbig {
    padding: 10px 12px 10px 76px;
    margin-left: 16px;
    border-radius: var(--radius-chip);
    background-color: var(--color-bg-selector);
    display: inline-block;
    font-size: 30px;
    line-height: 38px;
    vertical-align: 2px;
    position: relative;
    margin-top: 8px;
    color: var(--color-text-white);
    font-weight: bold;
  }

  .playerbig {
    margin-left: 20px;
    border-radius: var(--radius-lg);
    vertical-align: 8px;
  }

  /* Centred via top/transform rather than a hardcoded top per breakpoint.
     The chip's height depends on line-height, padding AND the bot badge's
     vertical-align: middle (which grows the line box ~2px), so fixed values
     drift: this one sat 4px high against an ideal ~10px. The breakpoints
     below only override size. */
  .flag {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 7px;
    width: 55px;
    height: 40px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }

  .level {
    padding: 0 6px;
    border-radius: var(--radius-md);
    background-color: var(--color-bg-badge);
    margin-left: 12px;
  }

  .level.bot {
    font-weight: lighter;
    font-size: var(--font-size-lg);
    vertical-align: middle;
    margin-left: 14px;
  }

  .positive {
    color: var(--color-score-positive);
    font-weight: 800;
    display: inline-block;
    margin-top: 10px;
  }

  .negative {
    color: var(--color-score-negative);
    font-weight: 800;
    display: inline-block;
    margin-top: 16px;
  }

  .upgrade {
    color: var(--color-text-heading);
    font-weight: 800;
    display: inline-block;
    margin-top: 10px;
  }

  .bold {
    font-weight: bold;
  }

  .pop {
    animation: itempop 0.3s ease-out 1;
  }

  /* Tablet */
  @media (min-width: 600px) and (max-width: ${unsafeCSS(NOTICE_COMPACT_MAX_WIDTH)}px) {
    .flag {
      left: 9px;
      width: 40px;
      height: 30px;
      background-size: 40px auto;
    }

    .player,
    .playerbig {
      font-size: 26px;
      line-height: 32px;
      padding: 8px 10px 8px 60px;
    }
  }

  /* Phone, portrait or landscape. After the tablet rule on purpose: a
     landscape phone matches both, and the phone sizes must win. */
  @media (max-width: ${unsafeCSS(NOTICE_COLUMN_MAX_PX)}px), (max-height: ${unsafeCSS(NOTICE_COLUMN_MAX_PX)}px) {
    .flag {
      left: 8px;
      width: 20px;
      height: 15px;
      background-size: 20px auto;
    }

    .player,
    .playerbig {
      font-size: var(--font-size-xl);
      line-height: 28px;
      padding: 6px 8px 6px 36px;
      margin-left: 10px;
    }

    /* In airmash-game's notice column (.notice-lanes): the host is a row of
       the column and the notice a block in it, centred by its margins, with
       no placement of its own and no translate in its pop and fade. Each
       consumer zeroes its top and left here too, as its own sheet comes
       after this one. */
    :host {
      position: relative;
      height: auto;
    }

    .notice {
      position: relative;
      top: auto;
      left: auto;
      margin: 0 auto 8px;
      transform: none;
      animation-name: msgshow-lane;
    }

    .notice.hiding {
      animation-name: msghide-lane;
    }
  }

  @keyframes msgshow-lane {
    0% {
      opacity: 1;
    }
    1% {
      transform: scale(1);
    }
    30% {
      transform: scale(1.25);
    }
    100% {
      transform: scale(1);
    }
  }

  @keyframes msghide-lane {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
`;
